import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  CheckCircle2,
  CircleAlert,
  Download,
  FileCheck2,
  History,
  MailCheck,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  cancelStatementSend,
  downloadStatementSendReport,
  getStatementSendDetail,
  getStatementSendReports,
  listStatementSendQueue,
  prepareStatementSend,
  retryStatementSend,
  submitStatementSend,
  type StatementSendAttempt,
  type StatementSendDetail,
  type StatementSendPermissions,
  type StatementSendPreparation,
  type StatementSendQueueItem,
} from "../../lib/arRecoveryStatementSendApi";

type StatementSendWorkspaceProps = {
  onClose: () => void;
  initialStatementId?: string;
  initialClient?: string;
  initialCustomer?: string;
  initialAccount?: string;
  initialRecipient?: string;
  sourceWorkspace?: string;
};

const EMPTY_PERMISSIONS: StatementSendPermissions = {
  canViewQueue: false,
  canPrepare: false,
  canSendLatest: false,
  canSendHistorical: false,
  canEditSubject: false,
  canEditMessage: false,
  canSelectRecipients: false,
  canUseManualOverride: false,
  canUseCc: false,
  canUseBcc: false,
  canConfirmSend: false,
  canCancelSend: false,
  canRetry: false,
  canGovernResend: false,
  canViewHistory: false,
  canViewProviderResponse: false,
  canViewAudit: false,
  canExportReport: false,
};

const FIELD =
  "focus-ring min-w-0 w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2.5 text-sm font-semibold text-[#0A2540]";
const BUTTON =
  "focus-ring inline-flex items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3.5 py-2.5 text-xs font-black uppercase tracking-[0.1em] text-[#0A2540] transition hover:border-[#14B8A6] disabled:cursor-not-allowed disabled:opacity-45";

export function ARRecoveryStatementSendWorkspace({
  onClose,
  initialStatementId = "",
  initialClient = "",
  initialCustomer = "",
  initialAccount = "",
  initialRecipient = "",
  sourceWorkspace = "Statement Center",
}: StatementSendWorkspaceProps) {
  const [search, setSearch] = useState(initialStatementId);
  const [client, setClient] = useState(initialClient);
  const [customer, setCustomer] = useState(initialCustomer);
  const [account, setAccount] = useState(initialAccount);
  const [recipient, setRecipient] = useState(initialRecipient);
  const [statementStatus, setStatementStatus] = useState("");
  const [versionStatus, setVersionStatus] = useState("");
  const [recipientStatus, setRecipientStatus] = useState("");
  const [readiness, setReadiness] = useState("");
  const [sendStatus, setSendStatus] = useState("");
  const [failureCategory, setFailureCategory] = useState("");
  const [statementDate, setStatementDate] = useState("");
  const [generatedDate, setGeneratedDate] = useState("");
  const [lastSentDate, setLastSentDate] = useState("");
  const [sort, setSort] = useState("generated-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });
  const [items, setItems] = useState<StatementSendQueueItem[]>([]);
  const [selectedStatementId, setSelectedStatementId] = useState(initialStatementId);
  const [detail, setDetail] = useState<StatementSendDetail | null>(null);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [recipientRoles, setRecipientRoles] = useState<Record<string, "TO" | "CC" | "BCC">>({});
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [historicalReason, setHistoricalReason] = useState("");
  const [duplicateReason, setDuplicateReason] = useState("");
  const [manualRecipient, setManualRecipient] = useState({ email: "", name: "", role: "TO" as "TO" | "CC" | "BCC", reason: "" });
  const [activePreparation, setActivePreparation] = useState<StatementSendPreparation | null>(null);
  const [providerResult, setProviderResult] = useState<StatementSendAttempt | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [retryReason, setRetryReason] = useState("");
  const [reports, setReports] = useState<Awaited<ReturnType<typeof getStatementSendReports>>["reports"] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setPage(1);
  }, [
    search,
    client,
    customer,
    account,
    recipient,
    statementStatus,
    versionStatus,
    recipientStatus,
    readiness,
    sendStatus,
    failureCategory,
    statementDate,
    generatedDate,
    lastSentDate,
    sort,
  ]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsLoading(true);
      setError("");
      listStatementSendQueue({
        search,
        client,
        customer,
        account,
        recipient,
        statementStatus,
        versionStatus,
        recipientStatus,
        readiness,
        sendStatus,
        failureCategory,
        statementDate,
        generatedDate,
        lastSentDate,
        sort,
        page,
        pageSize,
      })
        .then((payload) => {
          if (cancelled) return;
          setItems(payload.items);
          setPermissions(payload.permissions);
          setPagination(payload.pagination);
          setSelectedStatementId((current) =>
            current && payload.items.some((item) => item.statementId === current)
              ? current
              : payload.items[0]?.statementId || "",
          );
        })
        .catch((nextError) => {
          if (!cancelled) {
            setItems([]);
            setError(nextError instanceof Error ? nextError.message : "Statement Send queue could not be loaded.");
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    search,
    client,
    customer,
    account,
    recipient,
    statementStatus,
    versionStatus,
    recipientStatus,
    readiness,
    sendStatus,
    failureCategory,
    statementDate,
    generatedDate,
    lastSentDate,
    sort,
    page,
    pageSize,
  ]);

  useEffect(() => {
    if (!selectedStatementId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setIsDetailLoading(true);
    setActivePreparation(null);
    setProviderResult(null);
    getStatementSendDetail(selectedStatementId)
      .then((payload) => {
        if (cancelled) return;
        setDetail(payload);
        setPermissions(payload.permissions);
        const activeVersion = payload.versions.find((version) => version.isActive) || payload.versions[0];
        setSelectedVersionId(activeVersion?.documentVersionId || "");
        setSubject(payload.template.subject);
        setMessageBody(payload.template.messageBody);
        const nextRoles: Record<string, "TO" | "CC" | "BCC"> = {};
        const validRecipients = payload.recipients.filter(
          (item) => item.status === "active" && !item.optOut && item.validationStatus === "Valid" && item.email,
        );
        validRecipients
          .filter((item) => item.associations.some((association) => association.isDefault))
          .forEach((item) => {
            const association = item.associations.find((candidate) => candidate.isDefault);
            nextRoles[item.recipientId] = normalizeRole(association?.deliveryRole);
          });
        if (!Object.values(nextRoles).includes("TO") && validRecipients[0]) nextRoles[validRecipients[0].recipientId] = "TO";
        setRecipientRoles(nextRoles);
      })
      .catch((nextError) => {
        if (!cancelled) {
          setDetail(null);
          setError(nextError instanceof Error ? nextError.message : "Statement Send detail could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedStatementId]);

  useEffect(() => {
    if (!permissions.canViewHistory) return;
    getStatementSendReports().then((payload) => setReports(payload.reports)).catch(() => setReports(null));
  }, [permissions.canViewHistory]);

  const selectedVersion = useMemo(
    () => detail?.versions.find((version) => version.documentVersionId === selectedVersionId) || null,
    [detail, selectedVersionId],
  );
  const selectedRecipientCount = Object.keys(recipientRoles).length + (manualRecipient.email ? 1 : 0);

  async function refreshAll() {
    if (!selectedStatementId) return;
    const [nextDetail, queue] = await Promise.all([
      getStatementSendDetail(selectedStatementId),
      listStatementSendQueue({
        search,
        client,
        customer,
        account,
        recipient,
        statementStatus,
        versionStatus,
        recipientStatus,
        readiness,
        sendStatus,
        failureCategory,
        statementDate,
        generatedDate,
        lastSentDate,
        sort,
        page,
        pageSize,
      }),
    ]);
    setDetail(nextDetail);
    setItems(queue.items);
    setPagination(queue.pagination);
    if (permissions.canViewHistory) setReports((await getStatementSendReports()).reports);
  }

  async function prepare() {
    if (!detail || !selectedVersion || isBusy) return;
    setIsBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await prepareStatementSend(detail.statement.statementId, {
        versionId: selectedVersion.documentVersionId,
        sourceWorkspace,
        recipientSelections: Object.entries(recipientRoles).map(([recipientId, role]) => ({ recipientId, role })),
        manualRecipients: manualRecipient.email ? [manualRecipient] : [],
        subject,
        messageBody,
        templateId: detail.template.templateId,
        templateVersion: detail.template.templateVersion,
        historicalSendReason: historicalReason,
        duplicateSendReason: duplicateReason,
      });
      setActivePreparation(result.preparation);
      setMessage(
        result.preparation.readiness.ready
          ? "Send preparation is ready. Review the final confirmation and explicitly confirm Send Now."
          : "Preparation completed with blocked readiness checks. No provider request was made.",
      );
      await refreshAll();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Statement Send preparation failed.");
    } finally {
      setIsBusy(false);
    }
  }

  async function confirmSend() {
    if (!detail || !activePreparation?.readiness.ready || isBusy) return;
    setIsBusy(true);
    setError("");
    try {
      const result = await submitStatementSend(detail.statement.statementId, activePreparation.preparationId);
      setProviderResult(result.attempt);
      setMessage(
        result.replay
          ? "This confirmation was already processed. The existing delivery attempt was returned without sending again."
          : `Provider submission recorded as ${statementSendStatusLabel(result.attempt.status)}.`,
      );
      await refreshAll();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Provider submission could not be completed.");
    } finally {
      setIsBusy(false);
    }
  }

  async function cancelPreparation() {
    if (!detail || !activePreparation || !cancellationReason || isBusy) return;
    setIsBusy(true);
    setError("");
    try {
      const result = await cancelStatementSend(detail.statement.statementId, activePreparation.preparationId, cancellationReason);
      setActivePreparation(result.preparation);
      setMessage("Prepared Send cancelled before provider submission.");
      await refreshAll();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Prepared Send could not be cancelled.");
    } finally {
      setIsBusy(false);
    }
  }

  async function retryAttempt(attempt: StatementSendAttempt) {
    if (!detail || !retryReason || isBusy) return;
    setIsBusy(true);
    setError("");
    try {
      const result = await retryStatementSend(detail.statement.statementId, attempt.attemptId, retryReason);
      setActivePreparation(result.preparation);
      setMessage("A linked single-delivery retry preparation was created. Review readiness before confirming.");
      await refreshAll();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Retry preparation could not be created.");
    } finally {
      setIsBusy(false);
    }
  }

  async function exportReport() {
    try {
      const result = await downloadStatementSendReport();
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Send report could not be exported.");
    }
  }

  function clearFilters() {
    setSearch("");
    setClient("");
    setCustomer("");
    setAccount("");
    setRecipient("");
    setStatementStatus("");
    setVersionStatus("");
    setRecipientStatus("");
    setReadiness("");
    setSendStatus("");
    setFailureCategory("");
    setStatementDate("");
    setGeneratedDate("");
    setLastSentDate("");
    setSort("generated-desc");
  }

  return (
    <div className="mt-3 grid min-w-0 max-w-full gap-3 overflow-hidden text-[#0A2540]" data-statement-send-workspace="single-delivery">
      <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Operations</p>
            <h1 className="mt-1 break-words font-display text-[1.65rem] font-bold leading-tight">Statement Send Now</h1>
            <p className="mt-1 max-w-4xl text-sm font-semibold leading-5 text-[#475569]">
              Deliver one existing immutable Statement PDF through a validated, auditable provider submission.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void refreshAll()} className={BUTTON} disabled={!selectedStatementId}>
              <RefreshCw className="size-4" aria-hidden="true" /> Refresh
            </button>
            <button type="button" onClick={onClose} className={BUTTON}>
              <X className="size-4" aria-hidden="true" /> Return to Statement Center
            </button>
          </div>
        </div>
      </section>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {message ? <Notice tone="success">{message}</Notice> : null}

      <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-3.5 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(4,minmax(9rem,1fr))]">
          <label className="relative min-w-0">
            <span className="sr-only">Search Send Queue</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
            <input className={`${FIELD} pl-11`} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Statement, customer, account, recipient, provider ID" />
          </label>
          <input className={FIELD} value={client} onChange={(event) => setClient(event.target.value)} placeholder="Client" aria-label="Filter by client" />
          <input className={FIELD} value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="Customer" aria-label="Filter by customer" />
          <input className={FIELD} value={account} onChange={(event) => setAccount(event.target.value)} placeholder="Account / placement" aria-label="Filter by account or placement" />
          <input className={FIELD} value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="Recipient" aria-label="Filter by recipient" />
        </div>
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-[repeat(6,minmax(8rem,1fr))]">
          <FilterSelect label="Statement status" value={statementStatus} onChange={setStatementStatus} options={["Generated", "Sent", "Archived"]} />
          <FilterSelect label="Version status" value={versionStatus} onChange={setVersionStatus} options={["Generated", "Corrected", "Superseded", "Archived"]} />
          <FilterSelect label="Recipient status" value={recipientStatus} onChange={setRecipientStatus} options={["active", "inactive"]} />
          <FilterSelect label="Readiness" value={readiness} onChange={setReadiness} options={["Ready", "Not Ready"]} />
          <FilterSelect label="Send status" value={sendStatus} onChange={setSendStatus} options={["Never Sent", "Previously Sent", "ACCEPTED", "PARTIALLY_ACCEPTED", "REJECTED", "FAILED_RETRYABLE", "FAILED_FINAL"]} />
          <input className={FIELD} value={failureCategory} onChange={(event) => setFailureCategory(event.target.value)} placeholder="Failure category" aria-label="Filter by failure category" />
        </div>
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-[repeat(3,minmax(9rem,1fr))_minmax(13rem,1.4fr)_8rem_auto]">
          <LabeledInput label="Statement date" type="date" value={statementDate} onChange={setStatementDate} />
          <LabeledInput label="Generated date" type="date" value={generatedDate} onChange={setGeneratedDate} />
          <LabeledInput label="Last sent date" type="date" value={lastSentDate} onChange={setLastSentDate} />
          <FilterSelect label="Sort" value={sort} onChange={setSort} blank={false} options={["generated-desc", "statement-number-asc", "customer-asc", "statement-date-desc", "balance-desc", "last-sent-desc", "send-count-desc", "delivery-status-asc"]} />
          <FilterSelect label="Page size" value={String(pageSize)} onChange={(value) => setPageSize(Number(value))} blank={false} options={["25", "50", "100"]} />
          <button type="button" onClick={clearFilters} className={BUTTON}>Clear filters</button>
        </div>
      </section>

      <section className="grid min-w-0 gap-3 xl:grid-cols-[minmax(19rem,0.78fr)_minmax(0,1.55fr)]">
        <div className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-3.5 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Send Queue</p>
              <p className="mt-1 text-xs font-semibold text-[#475569]">{pagination.total} server-authoritative records</p>
            </div>
            <MailCheck className="size-5 text-[#14B8A6]" aria-hidden="true" />
          </div>
          <div className="mt-3 grid min-w-0 gap-2">
            {isLoading ? <p className="text-sm font-semibold text-[#475569]">Loading Send Queue...</p> : null}
            {!isLoading && !items.length ? <p className="rounded-xl border border-dashed border-[#BFEAFF] p-4 text-sm font-semibold text-[#475569]">No Statements match the active Send filters.</p> : null}
            {items.map((item) => (
              <button
                key={item.statementId}
                type="button"
                onClick={() => setSelectedStatementId(item.statementId)}
                aria-pressed={selectedStatementId === item.statementId}
                className={`focus-ring min-w-0 rounded-xl border p-3 text-left transition ${selectedStatementId === item.statementId ? "border-[#14B8A6] bg-[#EFFFFC]" : "border-[#BFEAFF] bg-[#F8FDFF] hover:border-[#14B8A6]"}`}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-black">{item.statementNumber}</p>
                    <p className="mt-1 break-words text-xs font-semibold text-[#475569]">{item.customerName} / {item.accountId}</p>
                  </div>
                  <StatusPill value={item.deliveryStatus || "Never Sent"} />
                </div>
                <div className="mt-2 grid min-w-0 grid-cols-2 gap-2 text-xs font-semibold text-[#475569]">
                  <span className="break-words">{item.clientCompany || "No client"}</span>
                  <span className="text-right">{formatCurrency(item.balanceCents)}</span>
                  <span>v{item.versionNumber || 1} / {item.documentStatus}</span>
                  <span className="text-right">{item.deliveryReadiness}</span>
                  <span className="break-all">{item.recipientEmail || "No recipient"}</span>
                  <span className="text-right">{item.sendCount} sends</span>
                </div>
                {item.failureReason ? <p className="mt-2 break-words text-xs font-bold text-rose-700">{item.failureReason}</p> : null}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <button type="button" className={BUTTON} disabled={!pagination.hasPrevious} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
            <span className="text-xs font-black uppercase tracking-[0.1em]">Page {pagination.page} of {pagination.totalPages}</span>
            <button type="button" className={BUTTON} disabled={!pagination.hasNext} onClick={() => setPage((current) => current + 1)}>Next</button>
          </div>
        </div>

        <div className="grid min-w-0 gap-3">
          {isDetailLoading ? <Notice>Loading Send Preparation Detail...</Notice> : null}
          {!isDetailLoading && !detail ? <Notice>Select a Statement from the Send Queue.</Notice> : null}
          {detail ? (
            <>
              <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Send Preparation Detail</p>
                    <h2 className="mt-1 break-words font-display text-xl font-bold">{detail.statement.statementNumber}</h2>
                    <p className="mt-1 break-words text-sm font-semibold text-[#475569]">{detail.statement.customerName} / {detail.statement.accountId}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusPill value={detail.statement.lifecycleStatus} />
                    <StatusPill value={detail.provider.configured ? `${detail.provider.adapter} Ready` : `${detail.provider.adapter} Not Configured`} />
                  </div>
                </div>
                <div className="mt-3 grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-4">
                  <Metric label="Client" value={detail.statement.clientCompany || "Not captured"} />
                  <Metric label="Customer number" value={detail.statement.customerNumber} />
                  <Metric label="Placement" value={detail.statement.placementId || "Not captured"} />
                  <Metric label="Statement amount" value={formatCurrency(detail.statement.balanceCents)} />
                </div>
              </section>

              <section className="grid min-w-0 gap-4 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                <div className="grid min-w-0 gap-3 md:grid-cols-2">
                  <label className="grid min-w-0 gap-1 text-xs font-black uppercase tracking-[0.1em] text-[#475569]">
                    Authorized immutable version
                    <select className={FIELD} value={selectedVersionId} onChange={(event) => { setSelectedVersionId(event.target.value); setActivePreparation(null); }}>
                      {detail.versions.map((version) => (
                        <option key={version.documentVersionId} value={version.documentVersionId}>
                          v{version.versionNumber} / {version.documentStatus}{version.isActive ? " / Active" : " / Historical"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Metric label="Attachment" value={selectedVersion ? `Statement-${detail.statement.statementNumber}-${detail.statement.statementDate || "undated"}.pdf` : "Version required"} />
                  <Metric label="Generated" value={formatDateTime(selectedVersion?.generatedAt || selectedVersion?.createdAt)} />
                  <Metric label="Immutable PDF" value={selectedVersion ? `${formatBytes(selectedVersion.fileSize)} / ${selectedVersion.mimeType || "Unknown MIME"}` : "Not resolved"} />
                  <Metric label="Checksum" value={selectedVersion?.contentHash || "Not captured"} mono />
                  <Metric label="Storage" value={selectedVersion?.storageKeyPresent ? `${selectedVersion.storageProvider || "R2"} object resolved` : "Object unavailable"} />
                </div>
                {selectedVersion && !selectedVersion.isActive ? (
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em] text-amber-800">
                    Historical version reason
                    <textarea className={FIELD} rows={2} required value={historicalReason} onChange={(event) => setHistoricalReason(event.target.value)} placeholder="Explain why this inactive version must be delivered." />
                  </label>
                ) : null}
              </section>

              <section className="grid min-w-0 gap-4 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Validated Recipients</p>
                  <p className="mt-1 text-xs font-semibold text-[#475569]">{selectedRecipientCount} selected; at least one TO recipient is required.</p>
                </div>
                <div className="grid min-w-0 gap-2">
                  {detail.recipients.map((item) => {
                    const blocked = item.status !== "active" || item.optOut || item.validationStatus !== "Valid" || !item.email;
                    const reason = item.optOut ? "Delivery opt-out" : item.status !== "active" ? "Inactive recipient" : item.validationIssues.join("; ") || "Recipient is not validated";
                    return (
                      <div key={item.recipientId} className={`grid min-w-0 gap-3 rounded-xl border p-3 md:grid-cols-[auto_minmax(0,1fr)_9rem] ${blocked ? "border-rose-200 bg-rose-50" : "border-[#BFEAFF] bg-[#F8FDFF]"}`}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${item.name || item.email}`}
                          checked={Boolean(recipientRoles[item.recipientId])}
                          disabled={blocked || !permissions.canSelectRecipients}
                          onChange={(event) => setRecipientRoles((current) => {
                            const next = { ...current };
                            if (event.target.checked) next[item.recipientId] = "TO";
                            else delete next[item.recipientId];
                            setActivePreparation(null);
                            return next;
                          })}
                          className="mt-1 size-4 accent-[#14B8A6]"
                        />
                        <div className="min-w-0">
                          <p className="break-words text-sm font-black">{item.name || "Unnamed recipient"}</p>
                          <p className="break-all text-xs font-semibold text-[#475569]">{item.email || "No email"}</p>
                          <p className="mt-1 break-words text-xs font-semibold text-[#475569]">{item.recipientType} / {item.source} / {item.validationStatus}</p>
                          {blocked ? <p className="mt-1 text-xs font-black text-rose-700">Blocked: {reason}</p> : null}
                        </div>
                        <select
                          aria-label={`Delivery role for ${item.name || item.email}`}
                          className={FIELD}
                          value={recipientRoles[item.recipientId] || "TO"}
                          disabled={!recipientRoles[item.recipientId]}
                          onChange={(event) => setRecipientRoles((current) => ({ ...current, [item.recipientId]: event.target.value as "TO" | "CC" | "BCC" }))}
                        >
                          <option value="TO">TO</option>
                          {permissions.canUseCc ? <option value="CC">CC</option> : null}
                          {permissions.canUseBcc ? <option value="BCC">BCC</option> : null}
                        </select>
                      </div>
                    );
                  })}
                </div>
                {permissions.canUseManualOverride ? (
                  <div className="grid min-w-0 gap-3 rounded-xl border border-dashed border-[#14B8A6] bg-[#F0FDFA] p-3 md:grid-cols-2 xl:grid-cols-[1fr_1.2fr_8rem]">
                    <LabeledInput label="One-time recipient name" value={manualRecipient.name} onChange={(value) => setManualRecipient((current) => ({ ...current, name: value }))} />
                    <LabeledInput label="One-time recipient email" type="email" value={manualRecipient.email} onChange={(value) => setManualRecipient((current) => ({ ...current, email: value }))} />
                    <FilterSelect label="Role" value={manualRecipient.role} onChange={(value) => setManualRecipient((current) => ({ ...current, role: value as "TO" | "CC" | "BCC" }))} blank={false} options={["TO", "CC", "BCC"]} />
                    <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em] text-[#475569] md:col-span-2 xl:col-span-3">
                      Manual override reason
                      <textarea className={FIELD} rows={2} value={manualRecipient.reason} onChange={(event) => setManualRecipient((current) => ({ ...current, reason: event.target.value }))} placeholder="Required when a one-time address is entered. Canonical recipient data is not changed." />
                    </label>
                  </div>
                ) : null}
              </section>

              <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Subject and Message Review</p>
                  <p className="mt-1 text-xs font-semibold text-[#475569]">Approved template: {detail.template.templateId} v{detail.template.templateVersion}</p>
                </div>
                <LabeledInput label="Subject" value={subject} onChange={(value) => { setSubject(value); setActivePreparation(null); }} disabled={!permissions.canEditSubject} />
                <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em] text-[#475569]">
                  Message body
                  <textarea className={FIELD} rows={7} value={messageBody} disabled={!permissions.canEditMessage} onChange={(event) => { setMessageBody(event.target.value); setActivePreparation(null); }} />
                </label>
                <div className="grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-4">
                  <Metric label="Provider" value={detail.provider.adapter} />
                  <Metric label="Sender" value={`${detail.provider.senderName} / ${detail.provider.senderEmail || "Not configured"}`} />
                  <Metric label="Reply-to" value={`${detail.provider.replyToName} / ${detail.provider.replyToEmail || "Not configured"}`} />
                  <Metric label="Provider scope" value="Immediate submission only; delivery tracking is deferred" />
                </div>
                <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em] text-[#475569]">
                  Duplicate send reason
                  <textarea className={FIELD} rows={2} value={duplicateReason} onChange={(event) => setDuplicateReason(event.target.value)} placeholder="Required only when the same version was recently sent to the same recipients." />
                </label>
                <button type="button" onClick={() => void prepare()} disabled={!permissions.canPrepare || !selectedVersion || isBusy} className={`${BUTTON} border-[#14B8A6] bg-[#14B8A6] text-white`}>
                  <ShieldCheck className="size-4" aria-hidden="true" /> {isBusy ? "Validating..." : "Validate and Prepare Send"}
                </button>
              </section>

              {activePreparation ? (
                <section className="grid min-w-0 gap-4 rounded-[1.35rem] border border-[#14B8A6] bg-[#F0FDFA] p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]" aria-live="polite">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">Final Send Confirmation</p>
                      <h3 className="mt-1 font-display text-xl font-bold">{statementSendStatusLabel(activePreparation.status)}</h3>
                    </div>
                    <StatusPill value={activePreparation.readiness.ready ? "Ready" : "Blocked"} />
                  </div>
                  <div className="grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-4">
                    <Metric label="Statement / version" value={`${detail.statement.statementNumber} / v${activePreparation.versionNumber}`} />
                    <Metric label="Recipients" value={activePreparation.recipients.accepted.map((item) => `${item.role}: ${item.email}`).join(", ") || "None"} />
                    <Metric label="Attachment" value={`${activePreparation.attachmentName} / ${formatBytes(activePreparation.attachmentSize)}`} />
                    <Metric label="Provider / sender" value={`${activePreparation.provider} / ${activePreparation.senderEmail || "Not configured"}`} />
                  </div>
                  <div className="grid min-w-0 gap-2 md:grid-cols-2">
                    {activePreparation.readiness.checks.map((item) => (
                      <div key={item.label} className={`flex min-w-0 items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold ${item.passed ? "border-emerald-200 bg-white text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
                        {item.passed ? <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" /> : <CircleAlert className="size-4 shrink-0" aria-hidden="true" />}
                        <span className="break-words">{item.label}</span>
                      </div>
                    ))}
                  </div>
                  {activePreparation.recipients.blocked.length ? (
                    <Notice tone="error">{activePreparation.recipients.blocked.map((item) => `${item.email || item.name}: ${item.reason}`).join("; ")}</Notice>
                  ) : null}
                  <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto]">
                    <input className={FIELD} value={cancellationReason} onChange={(event) => setCancellationReason(event.target.value)} placeholder="Cancellation reason" aria-label="Cancellation reason" />
                    <button type="button" className={BUTTON} disabled={!permissions.canCancelSend || !cancellationReason || !["DRAFT", "READY"].includes(activePreparation.status) || isBusy} onClick={() => void cancelPreparation()}>
                      Cancel Prepared Send
                    </button>
                    <button type="button" className={`${BUTTON} border-[#14B8A6] bg-[#0A2540] text-white`} disabled={!permissions.canConfirmSend || !activePreparation.readiness.ready || activePreparation.status !== "READY" || isBusy} onClick={() => void confirmSend()}>
                      <Send className="size-4" aria-hidden="true" /> Confirm Send Now
                    </button>
                  </div>
                </section>
              ) : null}

              <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Delivery History and Provider Response</p>
                    <p className="mt-1 text-xs font-semibold text-[#475569]">Provider acceptance is not final inbox delivery.</p>
                  </div>
                  <History className="size-5 text-[#14B8A6]" aria-hidden="true" />
                </div>
                {providerResult ? <Notice tone={/accepted/i.test(providerResult.status) ? "success" : "error"}>Latest confirmation: {statementSendStatusLabel(providerResult.status)}</Notice> : null}
                {!detail.history.length ? <p className="text-sm font-semibold text-[#475569]">No delivery attempts recorded for this Statement.</p> : null}
                {detail.history.map((attempt) => (
                  <article key={attempt.attemptId} className="grid min-w-0 gap-2 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-black">{statementSendStatusLabel(attempt.status)} / Attempt {attempt.attemptNumber}</p>
                        <p className="mt-1 break-words text-xs font-semibold text-[#475569]">{formatDateTime(attempt.requestedAt || attempt.createdAt)} / {attempt.provider || "Provider not recorded"}</p>
                      </div>
                      <StatusPill value={attempt.status} />
                    </div>
                    <div className="grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-4">
                      <Metric label="Provider message ID" value={attempt.providerMessageId || "Not returned"} mono />
                      <Metric label="Requested by" value={attempt.requestedBy || "Not recorded"} />
                      <Metric label="Correlation ID" value={attempt.correlationId} mono />
                      <Metric label="Failure" value={attempt.failureMessage || attempt.failureCategory || "None"} />
                    </div>
                    {attempt.providerResponse && permissions.canViewProviderResponse ? (
                      <p className="break-words rounded-lg border border-[#BFEAFF] bg-white px-3 py-2 text-xs font-semibold text-[#475569]">
                        {attempt.providerResponse.providerStatus || "Provider response"} / {attempt.providerResponse.responseCode || "No code"} / {attempt.providerResponse.safeSummary || "No summary"}
                      </p>
                    ) : null}
                    {attempt.status === "FAILED_RETRYABLE" && permissions.canRetry ? (
                      <div className="grid min-w-0 gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                        <input className={FIELD} value={retryReason} onChange={(event) => setRetryReason(event.target.value)} placeholder="Required retry reason" aria-label="Retry reason" />
                        <button type="button" className={BUTTON} disabled={!retryReason || isBusy} onClick={() => void retryAttempt(attempt)}>Prepare single retry</button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </section>

              {permissions.canViewAudit ? (
                <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Send Audit</p>
                  {!detail.audit.length ? <p className="text-sm font-semibold text-[#475569]">No Send audit events recorded.</p> : null}
                  {detail.audit.map((entry) => (
                    <div key={entry.auditId} className="grid min-w-0 gap-1 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3 md:grid-cols-[minmax(0,1fr)_auto]">
                      <div className="min-w-0">
                        <p className="break-words text-sm font-black">{humanize(entry.eventType)}</p>
                        <p className="mt-1 break-words text-xs font-semibold text-[#475569]">{entry.actorName || entry.actorRole} / {entry.sourceWorkspace}</p>
                      </div>
                      <div className="text-right text-xs font-semibold text-[#475569]">
                        <p>{humanize(entry.resultStatus)}</p>
                        <p>{formatDateTime(entry.occurredAt)}</p>
                      </div>
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {permissions.canViewHistory && reports ? (
        <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Operational Reporting</p>
              <p className="mt-1 text-xs font-semibold text-[#475569]">Submission outcomes only; final delivery, opens, and clicks are not represented.</p>
            </div>
            {permissions.canExportReport ? (
              <button type="button" className={BUTTON} onClick={() => void exportReport()}>
                <Download className="size-4" aria-hidden="true" /> Export CSV
              </button>
            ) : null}
          </div>
          <div className="grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-4">
            {Object.entries(reports.summary).map(([label, value]) => <Metric key={label} label={humanize(label)} value={String(value)} />)}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  blank = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  blank?: boolean;
}) {
  return (
    <label className="grid min-w-0 gap-1 text-[10px] font-black uppercase tracking-[0.1em] text-[#475569]">
      {label}
      <select className={FIELD} value={value} onChange={(event) => onChange(event.target.value)}>
        {blank ? <option value="">All</option> : null}
        {options.map((option) => <option key={option} value={option}>{humanize(option)}</option>)}
      </select>
    </label>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <label className="grid min-w-0 gap-1 text-[10px] font-black uppercase tracking-[0.1em] text-[#475569]">
      {label}
      <input className={FIELD} type={type} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Metric({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] px-3 py-2.5">
      <p className="truncate text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p>
      <p className={`mt-1 min-w-0 break-words text-sm font-semibold leading-5 text-[#0A2540] ${mono ? "font-mono text-xs" : ""}`}>{value || "Not captured"}</p>
    </div>
  );
}

function StatusPill({ value }: { value: string }) {
  const good = /ready|accepted|active|valid/i.test(value);
  const bad = /blocked|rejected|failed|invalid|not configured/i.test(value);
  return (
    <span className={`inline-flex max-w-full rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${good ? "border-emerald-200 bg-emerald-50 text-emerald-800" : bad ? "border-rose-200 bg-rose-50 text-rose-800" : "border-[#BFEAFF] bg-white text-[#0A2540]"}`}>
      <span className="min-w-0 break-words">{statementSendStatusLabel(value)}</span>
    </span>
  );
}

function statementSendStatusLabel(value: string) {
  const labels: Record<string, string> = {
    READY: "Ready to send",
    SUBMITTING: "Submitting to provider",
    SUBMITTED: "Submitted; acceptance pending",
    ACCEPTED: "Provider accepted; inbox delivery not confirmed",
    PARTIALLY_ACCEPTED: "Some recipients accepted",
    REJECTED: "Provider rejected",
    FAILED_RETRYABLE: "Temporary failure",
    FAILED_FINAL: "Final failure",
    CANCELLED: "Cancelled before submission",
  };
  return labels[value] || humanize(value);
}

function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "success" | "error" }) {
  const style = tone === "success"
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : tone === "error"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : "border-[#BFEAFF] bg-white text-[#0A2540]";
  return (
    <div className={`flex min-w-0 items-start gap-2 rounded-xl border p-3 text-sm font-semibold ${style}`}>
      {tone === "success" ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" /> : tone === "error" ? <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" /> : <FileCheck2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

function normalizeRole(value?: string): "TO" | "CC" | "BCC" {
  const normalized = String(value || "TO").toUpperCase();
  return normalized === "CC" || normalized === "BCC" ? normalized : "TO";
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function formatDateTime(value?: string | null) {
  return value ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Not recorded";
}

function formatBytes(value?: number | null) {
  if (!value) return "Not recorded";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
